import { LogManager } from '@src/utility/Logger';

import type {
  KeyChord,
  NormalizedKeyEvent,
  ScopeRegistration,
  ShortcutChord,
  ShortcutDefinition,
  ShortcutMatch,
  ShortcutRegistryLike,
} from './types';

const logger = LogManager.getLogger('shortcuts:registry');

interface RegisteredScope {
  id: string;
  scopeId: string;
  isActive: () => boolean;
  shortcuts: ShortcutDefinition[];
  order: number;
}

export class ShortcutRegistry implements ShortcutRegistryLike {
  private readonly registrations = new Map<string, RegisteredScope>();
  private orderCounter = 0;

  register(config: ScopeRegistration): () => void {
    const id = `scope-${++this.orderCounter}`;
    const record: RegisteredScope = {
      id,
      scopeId: config.scopeId,
      isActive: config.isActive,
      shortcuts: config.shortcuts,
      order: this.orderCounter,
    };

    this.registrations.set(id, record);

    return () => {
      this.registrations.delete(id);
    };
  }

  resolve(event: NormalizedKeyEvent): ShortcutMatch[] {
    const matches: ShortcutMatch[] = [];

    for (const registration of this.registrations.values()) {
      if (!this.invokeIsActive(registration)) continue;

      for (const definition of registration.shortcuts) {
        if (!this.matchesChord(event, definition.chord)) continue;
        if (!this.invokeGuard(definition)) continue;

        matches.push({
          scopeId: registration.scopeId,
          definition,
          priority: definition.priority ?? 0,
          order: registration.order,
        });
      }
    }

    if (matches.length <= 1) {
      return matches;
    }

    return matches.sort((a, b) => {
      if (b.priority !== a.priority) {
        return b.priority - a.priority;
      }
      return b.order - a.order;
    });
  }

  private invokeIsActive(registration: RegisteredScope): boolean {
    try {
      return registration.isActive();
    } catch (error) {
      logger.warn('shortcut scope isActive() threw', {
        scopeId: registration.scopeId,
        error,
      });
      return false;
    }
  }

  private invokeGuard(definition: ShortcutDefinition): boolean {
    if (!definition.guard) {
      return true;
    }

    try {
      return definition.guard() !== false;
    } catch (error) {
      logger.warn('shortcut guard threw', {
        shortcutId: definition.id,
        error,
      });
      return false;
    }
  }

  private matchesChord(
    event: NormalizedKeyEvent,
    chord: ShortcutChord,
  ): boolean {
    if (Array.isArray(chord)) {
      return chord.some(single => this.matchesSingleChord(event, single));
    }

    return this.matchesSingleChord(event, chord);
  }

  private matchesSingleChord(
    event: NormalizedKeyEvent,
    chord: KeyChord,
  ): boolean {
    if (event.code !== chord.code) {
      return false;
    }

    return (
      this.compareModifier(event.meta, chord.meta) &&
      this.compareModifier(event.ctrl, chord.ctrl) &&
      this.compareModifier(event.shift, chord.shift) &&
      this.compareModifier(event.alt, chord.alt)
    );
  }

  private compareModifier(actual: boolean, expected?: boolean): boolean {
    if (typeof expected === 'undefined') {
      return !actual;
    }

    return actual === expected;
  }
}
